<template>
  <div class="role-list-container">
    <div class="page-header">
      <div class="page-title-block">
        <h2>角色管理</h2>
        <p class="page-subtitle">配置系统中的角色身份，用于区分管理员、业务人员等权限</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="handleAdd">
          <i class="el-icon-plus"></i> 新增角色
        </el-button>
      </div>
    </div>

    <el-card class="search-card">
      <div class="search-form">
        <el-form :inline="true" :model="searchForm" class="search-inline-form">
          <el-form-item label="角色名称">
            <el-input v-model="searchForm.name" placeholder="请输入角色名称" clearable @input="handleSearch" />
          </el-form-item>
          <el-form-item label="角色编码">
            <el-input v-model="searchForm.code" placeholder="例如 ADMIN / USER" clearable @input="handleSearch" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              <i class="el-icon-search"></i> 搜索
            </el-button>
            <el-button @click="handleReset">
              <i class="el-icon-refresh"></i> 重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>

    <el-card>
      <!-- 桌面端表格 -->
      <el-table
        v-if="!isMobile"
        :data="filteredRoles"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="角色名称" min-width="150" />
        <el-table-column prop="code" label="角色编码" min-width="150" />
        <el-table-column prop="description" label="描述" min-width="180" />
        <el-table-column label="数据权限" min-width="120">
          <template slot-scope="scope">
            <span v-if="scope.row.dataScope === 'CUSTOM'">自定义部门</span>
            <span v-else>本部门</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="scope">
            <el-button size="small" type="success" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="small" @click="openMenuDialog(scope.row)">授权菜单</el-button>
            <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 手机端卡片列表 -->
      <div v-else class="role-mobile-list">
        <div
          v-for="item in filteredRoles"
          :key="item.id"
          class="role-mobile-card"
        >
          <div class="role-mobile-main">
            <div class="role-mobile-name">
              {{ item.name || '未命名角色' }}
            </div>
            <div class="role-mobile-code">
              编码：{{ item.code || '-' }}
            </div>
            <div class="role-mobile-desc" v-if="item.description">
              {{ item.description }}
            </div>
            <div class="role-mobile-meta">
              数据权限：{{ item.dataScope === 'CUSTOM' ? '自定义部门' : '本部门' }}
            </div>
          </div>
          <div class="role-mobile-actions">
            <el-button size="mini" type="success" @click="handleEdit(item)">编辑</el-button>
            <el-button size="mini" @click="openMenuDialog(item)">授权菜单</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(item)">删除</el-button>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 授权菜单对话框 -->
    <el-dialog title="授权菜单" :visible.sync="menuDialogVisible" :width="isMobile ? '95%' : '640px'">
      <div class="menu-list">
        <el-checkbox-group v-model="selectedMenuIds" class="menu-grid">
          <el-checkbox
            v-for="menu in allMenus"
            :key="menu.id"
            :label="menu.id"
            class="menu-item"
          >
            <div class="menu-item-main">
              <div class="menu-item-name">
                {{ menu.name }}
              </div>
              <div class="menu-item-path">
                {{ menu.path }}
              </div>
            </div>
          </el-checkbox>
        </el-checkbox-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="menuDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleMenuSubmit">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" :width="isMobile ? '92%' : '560px'">
      <!-- :key 强制每次打开弹窗时重建表单，避免 resetFields 把值恢复到「上次编辑」的 initialValue（Element UI 机制） -->
      <el-form :key="roleFormKey" :model="roleForm" :rules="roleRules" ref="roleForm" label-width="108px">
        <el-form-item label="角色名称" prop="name">
          <el-input v-model="roleForm.name" placeholder="请输入角色名称" />
        </el-form-item>
        <el-form-item label="角色编码" prop="code">
          <el-input v-model="roleForm.code" placeholder="例如 ADMIN / MANAGER / USER" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="roleForm.description" type="textarea" :rows="3" placeholder="请输入角色描述" />
        </el-form-item>
        <el-form-item label="数据权限" prop="dataScope">
          <el-radio-group v-model="roleForm.dataScope">
            <el-radio label="CURRENT_DEPT">仅查看本部门数据</el-radio>
            <el-radio label="CUSTOM">自定义部门</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="roleForm.dataScope === 'CUSTOM'" label="查看的部门">
          <el-checkbox-group v-model="roleForm.dataDepartmentIds" class="dept-checkbox-group">
            <el-checkbox v-for="d in departments" :key="d.id" :label="d.id">{{ d.name }}</el-checkbox>
          </el-checkbox-group>
          <p class="dept-hint">勾选该角色可查看哪些部门下用户负责的客户数据</p>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'RoleList',
  data() {
    return {
      roles: [],
      filteredRoles: [],
      isMobile: false,
      dialogVisible: false,
      isEditMode: false,
      dialogTitle: '',
      /** 递增以重建 el-form，防止先编辑后新增时 resetFields 回填旧角色数据 */
      roleFormKey: 0,
      departments: [],
      roleForm: {
        id: '',
        name: '',
        code: '',
        description: '',
        dataScope: 'CURRENT_DEPT',
        dataDepartmentIds: []
      },
      searchForm: {
        name: '',
        code: ''
      },
      roleRules: {
        name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
        code: [{ required: true, message: '请输入角色编码', trigger: 'blur' }]
      },
      // 授权菜单相关
      menuDialogVisible: false,
      allMenus: [],
      currentRoleId: null,
      selectedMenuIds: []
    }
  },
  mounted() {
    this.loadRoles()
    this.loadDepartments()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    loadRoles() {
      request.get('/roles')
        .then(res => {
          this.roles = res.data || res
          this.filteredRoles = this.roles
        })
        .catch(err => {
          console.error('加载角色列表失败:', err)
          this.$message.error('加载角色列表失败')
        })
    },
    loadDepartments() {
      request.get('/departments')
        .then(res => {
          this.departments = res.data || res
        })
        .catch(err => {
          console.error('加载部门失败:', err)
        })
    },
    handleSearch() {
      let list = [...this.roles]
      if (this.searchForm.name) {
        list = list.filter(r => r.name && r.name.includes(this.searchForm.name))
      }
      if (this.searchForm.code) {
        list = list.filter(r => r.code && r.code.includes(this.searchForm.code))
      }
      this.filteredRoles = list
    },
    handleReset() {
      this.searchForm = { name: '', code: '' }
      this.filteredRoles = this.roles
    },
    handleAdd() {
      this.isEditMode = false
      this.dialogTitle = '新增角色'
      this.roleFormKey += 1
      this.roleForm = {
        id: '',
        name: '',
        code: '',
        description: '',
        dataScope: 'CURRENT_DEPT',
        dataDepartmentIds: []
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.roleForm) {
          this.$refs.roleForm.clearValidate()
        }
      })
    },
    handleEdit(row) {
      this.isEditMode = true
      this.dialogTitle = '编辑角色'
      const ids = row.dataDepartmentIds
      this.roleFormKey += 1
      this.roleForm = {
        ...row,
        dataScope: row.dataScope || 'CURRENT_DEPT',
        dataDepartmentIds: Array.isArray(ids) ? ids.slice() : []
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.roleForm) {
          this.$refs.roleForm.clearValidate()
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`确定要删除角色 "${row.name}" 吗？`, '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request.delete(`/roles/${row.id}`)
          .then(() => {
            this.$message.success('删除角色成功')
            this.loadRoles()
          })
          .catch(err => {
            console.error('删除角色失败:', err)
            this.$message.error('删除角色失败')
          })
      }).catch(() => {})
    },
    handleSubmit() {
      this.$refs.roleForm.validate(valid => {
        if (!valid) return
        if (this.roleForm.dataScope === 'CUSTOM' &&
            (!this.roleForm.dataDepartmentIds || this.roleForm.dataDepartmentIds.length === 0)) {
          this.$message.warning('自定义数据权限时请至少选择一个部门')
          return
        }
        const payload = {
          name: this.roleForm.name,
          code: this.roleForm.code,
          description: this.roleForm.description || '',
          dataScope: this.roleForm.dataScope || 'CURRENT_DEPT',
          dataDepartmentIds: this.roleForm.dataScope === 'CUSTOM'
            ? (this.roleForm.dataDepartmentIds || [])
            : []
        }
        const req = this.isEditMode
          ? request.put(`/roles/${this.roleForm.id}`, payload)
          : request.post('/roles', payload)
        req.then(() => {
          this.$message.success(this.isEditMode ? '编辑角色成功' : '新增角色成功')
          this.dialogVisible = false
          this.loadRoles()
        }).catch(err => {
          console.error('保存角色失败:', err)
          this.$message.error('保存角色失败')
        })
      })
    },
    openMenuDialog(role) {
      this.currentRoleId = role.id
      this.menuDialogVisible = true
      // 先加载全部菜单，再加载角色已关联菜单
      request.get('/menus')
        .then(res => {
          this.allMenus = res.data || res
          return request.get(`/roles/${role.id}/menus`)
        })
        .then(res => {
          const roleMenus = res.data || res
          this.selectedMenuIds = (roleMenus || []).map(m => m.id)
        })
        .catch(err => {
          console.error('加载角色菜单失败:', err)
          this.$message.error('加载角色菜单失败')
        })
    },
    handleMenuSubmit() {
      if (!this.currentRoleId) return
      request.put(`/roles/${this.currentRoleId}/menus`, this.selectedMenuIds)
        .then(() => {
          this.$message.success('菜单授权成功')
          this.menuDialogVisible = false
        })
        .catch(err => {
          console.error('菜单授权失败:', err)
          this.$message.error('菜单授权失败')
        })
    }
  }
}
</script>

<style scoped>
.role-list-container {
  width: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding: 14px 4px 6px;
}

.page-title-block h2 {
  margin: 0;
  font-size: 22px;
  color: #0f8f59;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #80848f;
}

.search-card {
  margin-bottom: 20px;
}

.search-inline-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.role-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.role-mobile-card {
  border-radius: 10px;
  border: 1px solid #ebeef5;
  padding: 10px 12px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 159, 215, 0.04);
}

.role-mobile-main {
  margin-bottom: 8px;
}

.role-mobile-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.role-mobile-code {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}

.role-mobile-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
}

.role-mobile-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.dept-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}

.dept-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
}

.role-mobile-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.menu-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 12px;
  background: #f9fafc;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  grid-gap: 8px;
}

.menu-item {
  margin: 0;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  background: #ffffff;
  padding: 6px 10px;
  transition: all 0.2s ease;
}

.menu-item .el-checkbox__label {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.menu-item-main {
  display: flex;
  flex-direction: column;
}

.menu-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 2px;
}

.menu-item-path {
  font-size: 12px;
  color: #909399;
}

.menu-item:hover {
  border-color: var(--kh-primary, #17b676);
  box-shadow: 0 2px 8px rgba(23, 182, 118, 0.16);
}

@media (max-width: 768px) {
  .role-list-container {
    padding: 0 4px;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .search-inline-form {
    flex-direction: column;
  }
  .menu-grid {
    grid-template-columns: 1fr;
  }
  .role-mobile-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>

